import React, { useEffect, useState } from 'react';
import { formatFileName } from '../utilities/formatFileName';

// Helper function to ensure filename has .pdf extension
const ensurePdfExtension = (filename) => {
  if (!filename) return 'document.pdf';
  const name = filename.toLowerCase();
  if (name.endsWith('.pdf')) return filename;
  return `${filename}.pdf`;
};

const PdfViewer = ({ selectedFile }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [iframeError, setIframeError] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect if user is on mobile device
  useEffect(() => {
    const checkMobile = () => {
      const mobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      setIsMobile(mobile);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Function to open PDF - better approach for mobile
  const openPdf = () => {
    if (blobUrl) {
      // For mobile, directly navigate to the blob URL
      // This will open the PDF in the browser's native PDF viewer
      if (isMobile) {
        window.location.href = blobUrl;
      } else {
        // For desktop, open in new tab with iframe
        const newWindow = window.open();
        if (newWindow) {
          newWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>${formatFileName(selectedFile.Name)}</title>
                <style>
                  body { margin: 0; padding: 0; }
                  iframe { width: 100vw; height: 100vh; border: none; }
                </style>
              </head>
              <body>
                <iframe src="${blobUrl}" type="application/pdf"></iframe>
              </body>
            </html>
          `);
          newWindow.document.close();
        } else {
          alert('Pop-up blev blokeret. Tillad venligst pop-ups for denne side.');
        }
      }
    }
  };

  useEffect(() => {
    if (!selectedFile) {
      setBlobUrl(null);
      setLoading(false);
      setFetchError(false);
      setIframeError(false);
      return;
    }

    const displayPdf = async () => {
      setLoading(true);
      setFetchError(false);

      try {
        let pdfBase64;
        
        // Check if PDF content is already available (from direct file navigation)
        if (selectedFile._pdfContentFetched && selectedFile.base64Content) {
          console.log("Using already fetched PDF content");
          pdfBase64 = selectedFile.base64Content;
        } else {
          // Fetch the file content from the Azure Function
          console.log("Fetching PDF content from Azure Function");
          const response = await fetch(`${process.env.REACT_APP_SHAREPOINT_FILE_CONTENT_FUNCTION_URL}?itemId=${selectedFile.Id}`);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const data = await response.json();
          console.log("Fetched File Content from Azure Function:", data);
          
          pdfBase64 = data.base64Content;
        }

        if (!pdfBase64) {
          console.warn('No Base64Content available.');
          setBlobUrl(null);
          setFetchError(true);
          setLoading(false);
          return;
        }

        // Validate base64 content
        if (!pdfBase64.trim()) {
          console.error('Empty base64 content received');
          setFetchError(true);
          setLoading(false);
          return;
        }

        // Check if it's a valid PDF by looking for PDF header
        try {
          const decodedBytes = atob(pdfBase64.substring(0, 20));
          if (!decodedBytes.startsWith('%PDF-')) {
            console.error('Invalid PDF format - missing PDF header');
            setFetchError(true);
            setLoading(false);
            return;
          }
        } catch (decodeError) {
          console.error('Invalid base64 content:', decodeError);
          setFetchError(true);
          setLoading(false);
          return;
        }

        console.log(`PDF base64 content length: ${pdfBase64.length} characters`);
        console.log(`PDF starts with: ${pdfBase64.substring(0, 50)}...`);
        
        // Always use Blob URL for better compatibility
        try {
          const binaryString = atob(pdfBase64);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: 'application/pdf' });
          const dataUrl = URL.createObjectURL(blob);
          console.log('Blob URL created');
          setBlobUrl(dataUrl);
        } catch (blobError) {
          console.error('Failed to create blob URL:', blobError);
          // Fallback to data URL
          const dataUrl = `data:application/pdf;base64,${pdfBase64}`;
          setBlobUrl(dataUrl);
        }
        
        // Add smart iframe monitoring that doesn't trigger false errors (desktop only)
        if (!isMobile) {
          setTimeout(() => {
            const iframe = document.querySelector('iframe[title="PDF Viewer"]');
            if (iframe) {
              console.log('Setting up iframe monitoring...');
              
              let hasLoaded = false;
              let monitoringActive = true;
              
              iframe.onload = () => {
                console.log('PDF iframe onload event fired');
                hasLoaded = true;
                if (monitoringActive) {
                  setIframeError(false);
                }
              };
              
              iframe.onerror = (error) => {
                console.error('PDF iframe onerror event fired:', error);
                if (monitoringActive) {
                  setIframeError(true);
                }
              };
              
              // Check if iframe appears to be working after 3 seconds
              setTimeout(() => {
                if (monitoringActive) {
                  console.log('3-second status check:');
                  console.log('- Has onload fired:', hasLoaded);
                  console.log('- Iframe dimensions:', iframe.offsetWidth, iframe.offsetHeight);
                  
                  // If iframe has reasonable dimensions, consider it working
                  if (iframe.offsetWidth > 100 && iframe.offsetHeight > 100) {
                    console.log('Iframe appears to be working based on size check');
                    hasLoaded = true;
                    setIframeError(false);
                  }
                }
              }, 3000);
              
              // Only show error if truly problematic after 10 seconds
              setTimeout(() => {
                if (monitoringActive && !hasLoaded) {
                  console.warn('PDF iframe still not confirmed loaded after 10 seconds');
                  // Final check: if iframe has reasonable size, don't show error
                  if (iframe.offsetWidth > 100 && iframe.offsetHeight > 100) {
                    console.log('Iframe has good dimensions, assuming it is working');
                  } else {
                    console.log('Iframe appears to have failed loading');
                    setIframeError(true);
                  }
                }
                monitoringActive = false;
              }, 10000);
            }
          }, 200);
        }
        
      } catch (err) {
        console.error('Error displaying PDF:', err);
        setBlobUrl(null);
        setFetchError(true);
      } finally {
        setLoading(false);
      }
    };

    displayPdf();

    return () => {
      if (blobUrl) {
        // Clean up blob URLs to prevent memory leaks
        if (blobUrl.startsWith('blob:')) {
          URL.revokeObjectURL(blobUrl);
        }
        setBlobUrl(null);
      }
    };
  }, [selectedFile, isMobile]);

  if (!selectedFile) {
    return (
      <main className="flex-1 p-6 flex items-center justify-center text-gray-500">
        Venligst vælg en fil fra sidemenuen.
      </main>
    );
  }

  return (
    <main className="flex-1 h-full p-6 flex flex-col overflow-hidden">
      <h1 className="text-2xl font-semibold text-gray-800 mb-4">
        {formatFileName(selectedFile.Name)}
      </h1>

     <div className="self-start mb-4">
        {selectedFile.Historisk === "False" || selectedFile.Historisk === "0" ? (
          <div
            className="inline-block rounded-full px-3 py-1 text-sm font-semibold text-white"
            style={{ backgroundColor: 'green' }}
          >
            GÆLDENDE
          </div>
        ) : selectedFile.Historisk === "True" || selectedFile.Historisk === "1" ? (
          <div
            className="inline-block rounded-full px-3 py-1 text-sm font-semibold text-white"
            style={{ backgroundColor: 'red' }}
          >
            HISTORISK
          </div>
        ) : null}
      </div>

      <div className="flex-1 flex items-center justify-center border border-gray-300 rounded shadow overflow-hidden relative">
        {loading ? (
          <div className="flex flex-col items-center justify-center space-y-2">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-500"></div>
            <p className="text-gray-500 text-sm">Indlæser PDF...</p>
          </div>
        ) : (fetchError || iframeError || isMobile) ? (
          <div className="text-center p-8">
            {!isMobile && (
              <div className="text-red-600 mb-4">
                <p className="text-lg font-semibold">PDF kunne ikke vises</p>
                <p className="text-sm">
                  {fetchError ? 'Fejl ved hentning af fil' : 'Filen kan ikke vises i browseren'}
                </p>
              </div>
            )}
            {isMobile && (
              <div className="text-gray-700 mb-4">
                <p className="text-lg font-semibold mb-2">PDF er klar til visning</p>
                <p className="text-sm mb-4">
                  Mobile browsere understøtter ikke embedded PDFs fuldt ud.
                </p>
                <p className="text-sm text-gray-600">
                  Klik på "Vis PDF" for at åbne filen i browserens PDF-viewer, eller download filen.
                </p>
              </div>
            )}
            {blobUrl && (
              <div className="space-y-3">
                <button
                  onClick={openPdf}
                  className="inline-flex items-center px-6 py-3 text-white rounded-md hover:opacity-90 focus:outline-none text-lg font-semibold"
                  style={{ backgroundColor: 'rgb(238, 123, 25)' }}
                >
                  {isMobile ? 'Vis PDF' : 'Åbn i ny fane'}
                </button>
                <div>
                  <a
                    href={blobUrl}
                    download={ensurePdfExtension(selectedFile.Name)}
                    className="inline-flex items-center px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none"
                    style={{ backgroundColor: 'rgb(238, 123, 25)' }}
                  >
                    Download PDF
                  </a>
                </div>
              </div>
            )}
          </div>
        ) : (
          blobUrl && (
            <div className="w-full h-full flex flex-col">
              <iframe
                src={blobUrl}
                title="PDF Viewer"
                className="w-full flex-1 rounded"
                allow="fullscreen"
                allowFullScreen={true}
              />
              {/* Always show backup options at the bottom */}
              <div className="bg-gray-50 border-t p-3 flex justify-center space-x-4 text-sm">
                <button
                  onClick={openPdf}
                  className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none"
                >
                  Åbn i ny fane
                </button>
                <span className="text-gray-300">|</span>
                <a
                  href={blobUrl}
                  download={ensurePdfExtension(selectedFile.Name)}
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  Download PDF
                </a>
              </div>
            </div>
          )
        )}
      </div>
    </main>
  );
};

export default PdfViewer;